/**
 * RVRN 4-Card — scroll-mode active card detector + mode toggle.
 *
 * The track starts with .is-scroll-mode (button forced white).
 * JS detects whether the @container query has switched to grid
 * (via computed display === 'grid') and removes .is-scroll-mode
 * so button reverts to its component style (e.g. text-dark).
 */
(() => {
  const initSection = (section) => {
    const track = section.querySelector('[data-rvrn-4-card-track]');
    if (!track) return;
    const cards = [...track.querySelectorAll('.rvrn-4-card__item')];
    if (!cards.length) return;

    let rafId = 0;
    let hasInitialised = false;

    const isScrollMode = () => {
      const display = getComputedStyle(track).display;
      return display !== 'grid';
    };

    const updateMode = () => {
      if (isScrollMode()) {
        track.classList.add('is-scroll-mode');
      } else {
        track.classList.remove('is-scroll-mode');
      }
    };

    const clearActive = () => {
      cards.forEach((card) => card.classList.remove('is-active'));
    };

    const setActiveCard = () => {
      updateMode();
      if (!isScrollMode()) {
        clearActive();
        return;
      }
      const trackCenter = track.scrollLeft + track.clientWidth / 2;
      let activeCard = cards[0];
      let shortestDistance = Number.POSITIVE_INFINITY;
      cards.forEach((card) => {
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const distance = Math.abs(cardCenter - trackCenter);
        if (distance < shortestDistance) {
          shortestDistance = distance;
          activeCard = card;
        }
      });
      cards.forEach((card) => card.classList.toggle('is-active', card === activeCard));
    };

    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(setActiveCard);
    };

    const setInitialPosition = () => {
      updateMode();
      if (!isScrollMode()) {
        clearActive();
        hasInitialised = true;
        return;
      }
      const anchor = cards[1] || cards[0];
      if (anchor) {
        anchor.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
      }
      setActiveCard();
      hasInitialised = true;
    };

    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(setActiveCard);
    }, { passive: true });

    requestAnimationFrame(() => {
      if (document.readyState === 'complete') {
        setInitialPosition();
      } else {
        window.addEventListener('load', () => {
          if (!hasInitialised) setInitialPosition();
        }, { once: true });
        setInitialPosition();
      }
    });

    document.addEventListener('shopify:section:load', (event) => {
      if (section.contains(event.target)) setInitialPosition();
    });
    document.addEventListener('shopify:block:select', (event) => {
      if (!section.contains(event.target)) return;
      const target = event.target.closest('.rvrn-4-card__item');
      if (target && isScrollMode()) {
        target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    });
  };

  const init = () => {
    document.querySelectorAll('[data-rvrn-4-card]').forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector?.('[data-rvrn-4-card]')) {
      event.target.querySelectorAll('[data-rvrn-4-card]').forEach(initSection);
    }
  });
})();
